import { ReactElement, useRef, useState } from "react";
import Navbar from "react-bootstrap/Navbar";
import styled from "styled-components";
import { Permission, AuthUser, hasPermission } from "react-ai-platform-auth";
import {
  Avatar,
  ButtonGroup,
  IconClipboardListCheck,
  IconHome,
  IconRectangle,
  IconSignOut,
  NavBar,
  NavBarItem,
  TextLink,
  IconUser,
  IconUsers,
  IconCircleQuestion,
  IconCalendarClock,
  IconNetworkWired,
} from "@telusinternationalai/seibun-ui";
import { useLocation } from "react-router-dom";

import { useAuthContext } from "../../authHelper";
import {
  ACCOUNT,
  ASSESSMENT,
  LIONBRIDGE_CMP,
  ORGANIZATIONS,
  PROJECTS,
  SUPPORT,
  USERS,
  WALRUS,
} from "../../routes";
import userUtils from "../../utilities/userUtils";
import useClickOutsideEvent from "../../hooks/useClickOutsideEvent";

const LOGO_LINK = `${process.env.PUBLIC_URL}/img/telus-international-logo.png`;

export const StyledNavbarBrand = styled(Navbar.Brand)`
  && {
    padding: 0.3125rem 2rem 0rem 1.0625rem;
    margin-right: 0;
    &:before {
      display: block;
      content: "";
      background-image: url(${LOGO_LINK});
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
      width: 14.539375rem;
      height: 1.5rem;
    }
  }
`;

type HeaderProps = Readonly<{
  user: AuthUser;
}>;

const GlobalHeader = ({ user }: HeaderProps): ReactElement => {
  const { logout } = useAuthContext();
  const routeLocation = useLocation();
  const { fullName = "", userInitial, email, activeMembership } = user;
  const shouldUseCmpContributorSpecificFeatures = userUtils.shouldUseCmpContributorSpecificFeatures(
    user
  );
  const isTiMember = userUtils.belongsToTiOrganization(user);
  const [openUserProfile, setOpenUserProfile] = useState(false);

  const MAIN_NAVBAR_ITEMS: ReadonlyArray<NavBarItem> = [
    {
      icon: <IconHome height={40} width={40} />,
      title: "Home",
      path: LIONBRIDGE_CMP.HOME,
      isExternal: true,
      isSamePage: true,
      isHidden: !shouldUseCmpContributorSpecificFeatures,
    },
    hasPermission(user, Permission.READ_PROJECTS)
      ? {
          icon: <IconRectangle height={40} width={40} />,
          title: "Projects",
          path: PROJECTS.LIST,
          isActive: routeLocation.pathname.includes(PROJECTS.LIST),
        }
      : {
          icon: <IconRectangle height={40} width={40} />,
          title: "Find Jobs",
          path: LIONBRIDGE_CMP.FIND_JOBS,
          isSamePage: true,
          isActive: routeLocation.pathname.includes(LIONBRIDGE_CMP.FIND_JOBS),
        },
    {
      icon: <IconUsers height={40} width={40} isLight />,
      title: "Users",
      path: USERS.LIST,
      isHidden: !hasPermission(user, Permission.READ_USERS),
      isActive: routeLocation.pathname.includes(USERS.LIST),
    },
    {
      icon: <IconNetworkWired height={40} width={40} />,
      title: "Organizations",
      path: ORGANIZATIONS.LIST,
      isHidden: !hasPermission(user, Permission.READ_ORGS),
      isActive: routeLocation.pathname.includes(ORGANIZATIONS.LIST),
    },
    {
      icon: <IconClipboardListCheck isLight height={40} width={40} />,
      title: "Qualifications",
      path: ASSESSMENT.LIST,
      isActive: routeLocation.pathname.includes(ASSESSMENT.LIST),
    },
    {
      icon: <IconCalendarClock height={40} width={40} />,
      title: "Worksheets",
      path: WALRUS.WORKSHEETS,
      isExternal: true,
      isSamePage: true,
      isHidden: !isTiMember,
    },
  ];

  const SECONDARY_NAVBAR_ITEMS: ReadonlyArray<NavBarItem> = [
    shouldUseCmpContributorSpecificFeatures
      ? {
          icon: <IconCircleQuestion isLight height={40} width={40} />,
          title: "Support",
          path: LIONBRIDGE_CMP.SUPPORT,
          isExternal: true,
          isSamePage: true,
        }
      : {
          icon: <IconCircleQuestion isLight height={40} width={40} />,
          title: "Support",
          path: SUPPORT.FAQ,
          isActive: routeLocation.pathname.includes(SUPPORT.FAQ),
        },
  ];

  const wrapperRef = useRef<HTMLInputElement>(null);

  useClickOutsideEvent(wrapperRef, () => setOpenUserProfile(false), "click");

  return (
    <div className="tw-relative tw-flex-grow-0">
      <NavBar
        avatar={{
          onClick: (e) => {
            e.stopPropagation();
            setOpenUserProfile((value) => !value);
          },
          userInitials: userInitial,
        }}
        mainNavBarItems={MAIN_NAVBAR_ITEMS}
        secondaryNavBarItems={SECONDARY_NAVBAR_ITEMS}
      />
      {openUserProfile ? (
        <div
          ref={wrapperRef}
          className="tw-absolute tw-bottom-7 tw-left-full w-400 tw-z-20 tw-pl-2"
          aria-label="user-profile-dialog"
        >
          <div className="user-pop-out tw-rounded tw-p-6">
            <div className="tw-flex tw-flex-wrap tw-gap-2 tw-items-start">
              <div className="tw-rounded-full tw-flex tw-items-center tw-justify-center">
                <Avatar initials={userInitial} size="xlarge" />
              </div>
              <div>
                <div className="sui-text-b2 sui-font-medium sui-text-darkGray-darkest">
                  {fullName}
                </div>
                <div className="sui-text-b5 sui-text-lightGray-darkest">{email}</div>
                <div className="sui-text-b5 sui-text-darkGray tw-pt-4 tw-uppercase">
                  {userUtils.formatRoleDisplay(activeMembership.role)}
                </div>
              </div>
            </div>
            <hr className="sui-border-lightGray sui-my-base" />
            <ButtonGroup className="sui-flex sui-flex-col">
              {shouldUseCmpContributorSpecificFeatures ? (
                <TextLink
                  hasIcon={{
                    icon: (
                      <IconUser
                        variant="circle"
                        className="sui-h-base sui-w-base sui-mr-2 sui-text-darkGray"
                      />
                    ),
                    isLeft: true,
                  }}
                  className="sui-u-inherit-text-color"
                  href={LIONBRIDGE_CMP.USER_PROFILE}
                  target="_self"
                >
                  My Profile
                </TextLink>
              ) : (
                <TextLink
                  hasIcon={{
                    icon: (
                      <IconUser
                        variant="circle"
                        className="sui-h-base sui-w-base sui-mr-2 sui-text-darkGray"
                      />
                    ),
                    isLeft: true,
                  }}
                  className="sui-u-inherit-text-color"
                  to={ACCOUNT.USER_PROFILE}
                >
                  My Profile
                </TextLink>
              )}

              <TextLink
                hasIcon={{
                  icon: (
                    <IconSignOut
                      outline
                      className="sui-h-base sui-w-base sui-mr-2 sui-text-darkGray"
                    />
                  ),
                  isLeft: true,
                }}
                className="sui-u-inherit-text-color"
                onClick={async () => await logout(true)}
                role="link"
              >
                Sign Out
              </TextLink>
            </ButtonGroup>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default GlobalHeader;
